/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/QaItpView.tsx
 * FILE_ID: JOBHUB-QA-ITP-VIEW-001
 * PURPOSE: ITP mode body: selected areas as a plan. Export lives on the mode bar.
 */
import { useMemo, useState } from 'react'
import { FileOutput } from 'lucide-react'
import { ItpBrandLockup } from '../../qa/itpBrandLockup'
import { QaMergedAreas } from './useQaEvidenceUrls'
import { checklistGroups, groupEvidenceByItem } from '../../qa/qaApiHelpers'
import type { createQaApi, QaAreaExecution, QaItemStatus } from '../../qa/qaApi'
import { JobQaPackPanel } from '../JobQaPackPanel'
import { QaChecklistSectionHeader } from './QaChecklistSectionHeader'
import { RunScopePickers } from '../JobQaExecution'
import { QaItpItemCard } from './QaItpItemCard'

export function QaItpAreaBand({ level, area }: { level?: string; area: string }) {
  return (
    <div className="flex items-center justify-between gap-2 border-b border-border bg-muted px-2 py-1.5 text-xs font-semibold text-foreground">
      <span className="min-w-0 truncate">{level || ''}</span>
      <span className="min-w-0 truncate text-right">{area}</span>
    </div>
  )
}

export function QaItpExportButton({
  onExport,
  disabled = false,
}: {
  onExport: () => void
  disabled?: boolean
}) {
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      <button
        type="button"
        disabled={disabled}
        onClick={onExport}
        className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs font-medium disabled:opacity-50"
      >
        <FileOutput className="h-3.5 w-3.5" aria-hidden />
        Export
      </button>
    </div>
  )
}

export function QaItpView(props: Parameters<typeof QaItpViewBody>[0]) {
  return (
    <QaMergedAreas api={props.api} areas={props.areas} enabled>
      {(areas) => <QaItpViewBody {...props} areas={areas} />}
    </QaMergedAreas>
  )
}

function QaItpViewBody({
  areas,
  checklistIds = null,
  itemIds,
  showAll = false,
  onShowAll,
  jobId,
  inspectionId,
  api,
  onMutated,
  onSetStatus,
  statusPending = false,
  itemSelection,
  checklistSelection,
  onRenameChecklist,
  startRenameId,
  onRenameStarted,
  onWriteError,
  showPacks = true,
  pickerAreas,
  onSelectArea,
}: {
  areas: QaAreaExecution[]
  checklistIds?: string[] | null
  itemIds?: ReadonlySet<string>
  showAll?: boolean
  onShowAll?: () => void
  jobId: number
  inspectionId: string
  api: ReturnType<typeof createQaApi>
  onMutated: () => void
  onSetStatus: (itemId: string, status: QaItemStatus) => void
  statusPending?: boolean
  itemSelection: {
    isSelected: (id: string, checklistId?: string, areaId?: string) => boolean
    onToggle: (id: string, checklistId?: string, areaId?: string) => void
  }
  checklistSelection?: { isSelected: (id: string) => boolean; onToggle: (id: string) => void }
  onRenameChecklist?: (checklistId: string, title: string) => void
  startRenameId?: string | null
  onRenameStarted?: () => void
  onWriteError?: (message: string) => void
  showPacks?: boolean
  pickerAreas?: { id: string; name: string; group?: string }[]
  onSelectArea?: (areaId: string) => void
}) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  // Selecting a check must never remove its neighbours from the screen
  // (operator regression report, 2026-09-16): the list ALWAYS shows every
  // non-hold check. Selection only scopes the print/save payload.
  const groups = useMemo(() => {
    const wanted = checklistIds && checklistIds.length > 0 ? new Set(checklistIds) : null
    return areas.flatMap((area) =>
      checklistGroups(area, undefined)
        .filter((group) => !wanted || wanted.has(group.checklist.id))
        .map((group) => ({ area, group })),
    )
  }, [areas, checklistIds])
  const selectedCount = itemIds?.size ?? 0
  void showAll
  void onShowAll

  return (
    <div className="space-y-3">
      {pickerAreas && onSelectArea && areas[0] ? (
        // Reused from: tabs/JobQaExecution.tsx (RunScopePickers)
        <RunScopePickers
          hideChecklist
          areaName={areas[0].area_name}
          checklistName={null}
          areas={pickerAreas}
          checklists={[]}
          currentAreaId={areas[0].id}
          currentChecklistId={null}
          onSelectArea={onSelectArea}
          onSelectChecklist={() => undefined}
        />
      ) : null}
      <ItpBrandLockup />
      {selectedCount > 0 ? (
        <p className="text-xs text-muted-foreground">
          {selectedCount} selected — Print / Save can scope to the selection
        </p>
      ) : null}

      {groups.length === 0 ? (
        <p className="text-sm text-muted-foreground">No checks in this plan.</p>
      ) : groups.map(({ area, group }, index) => (
        <section key={`${area.id}:${group.checklist.id}`} className="space-y-2">
          {index === 0 || groups[index - 1].area.id !== area.id ? (
            <QaItpAreaBand level={area.floor_name} area={area.area_name} />
          ) : null}
          <QaChecklistSectionHeader
            title={group.name}
            done={group.done}
            total={group.total}
            expanded={!collapsed[group.checklist.id]}
            stickyClass="top-[var(--qa-section-top,0px)]"
            onToggle={() => setCollapsed((state) => ({
              ...state,
              [group.checklist.id]: !state[group.checklist.id],
            }))}
            selected={checklistSelection?.isSelected(group.checklist.id)}
            onToggleSelected={checklistSelection
              ? () => checklistSelection.onToggle(group.checklist.id)
              : undefined}
            onRename={onRenameChecklist
              ? (title) => onRenameChecklist(group.checklist.id, title)
              : undefined}
            startRename={startRenameId === group.checklist.id}
            onRenameStarted={onRenameStarted}
          />
          {!collapsed[group.checklist.id] && group.items.map((item) => (
            <QaItpItemCard
              key={item.id}
              item={item}
              evidence={groupEvidenceByItem(area.evidence ?? [])[item.id] ?? []}
              jobId={jobId}
              areaId={area.id}
              api={api}
              onMutated={onMutated}
              onSetStatus={onSetStatus}
              statusPending={statusPending}
              selected={itemSelection.isSelected(item.id, group.checklist.id, area.id)}
              onToggleSelected={() => itemSelection.onToggle(item.id, group.checklist.id, area.id)}
              onWriteError={onWriteError}
            />
          ))}
        </section>
      ))}

      {showPacks && (
        <details className="rounded-lg border border-border bg-muted/20 p-2">
          <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
            Archived packs and downloads
          </summary>
          <div className="mt-2">
            <JobQaPackPanel inspectionId={inspectionId} api={api} />
          </div>
        </details>
      )}
    </div>
  )
}
